// Service for managing resource preloading and performance optimization
import { safeRequestIdleCallback, safeSetFetchPriority } from '../utils/iosPolyfills';

interface PreloadResource {
  href: string;
  as: 'image' | 'font' | 'script' | 'style';
  type?: string;
  crossorigin?: 'anonymous' | 'use-credentials';
  fetchpriority?: 'high' | 'low' | 'auto';
}

class PreloadService {
  private preloadedResources = new Set<string>();

  /**
   * Preload a single resource
   */
  preloadResource(resource: PreloadResource): void {
    if (this.preloadedResources.has(resource.href)) {
      return; // Already preloaded
    }

    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource.href;
    link.as = resource.as;
    
    if (resource.type) link.type = resource.type;
    if (resource.crossorigin) link.crossOrigin = resource.crossorigin;
    if (resource.fetchpriority) {
      safeSetFetchPriority(link, resource.fetchpriority);
    }

    document.head.appendChild(link);
    this.preloadedResources.add(resource.href);
  }

  /**
   * Preload multiple resources
   */
  preloadResources(resources: PreloadResource[]): void {
    resources.forEach(resource => this.preloadResource(resource));
  }

  /**
   * Preload critical homepage resources
   */
  preloadHomepageResources(): void {
    const criticalResources: PreloadResource[] = [
      // Hero images
      {
        href: '/assets/images/mobile mockup1.webp',
        as: 'image',
        fetchpriority: 'high'
      },
      {
        href: '/assets/images/black Mastercard.webp',
        as: 'image',
        fetchpriority: 'high'
      },
      {
        href: '/assets/images/blue-light Mastercard.webp',
        as: 'image',
        fetchpriority: 'high'
      },
      // Critical fonts
      {
        href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
        as: 'font',
        type: 'font/woff2',
        crossorigin: 'anonymous',
        fetchpriority: 'high'
      },
      {
        href: 'https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrJJfecnFHGPc.woff2',
        as: 'font',
        type: 'font/woff2',
        crossorigin: 'anonymous',
        fetchpriority: 'high'
      }
    ];

    this.preloadResources(criticalResources);
  }

  /**
   * DNS prefetch for external domains
   */
  dnsPrefetch(domains: string[]): void {
    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);
    });
  }

  /**
   * Preconnect to external domains
   */
  preconnect(domains: string[]): void {
    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      if (domain.includes('fonts')) {
        link.crossOrigin = 'anonymous';
      }
      document.head.appendChild(link);
    });
  }

  /**
   * Initialize performance optimizations
   */
  initializePerformanceOptimizations(): void {
    // DNS prefetch for external resources
    this.dnsPrefetch([
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://www.google-analytics.com',
      'https://www.googletagmanager.com'
    ]);

    // Preconnect to critical external domains
    this.preconnect([
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com'
    ]);

    // Optimize font loading
    this.optimizeFontLoading();
  }

  /**
   * Optimize font loading to prevent FOUT/FOIT
   */
  private optimizeFontLoading(): void {
    // Add font-display: swap to existing font links
    const fontLinks = document.querySelectorAll('link[href*="fonts.googleapis.com"]');
    fontLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (href && !href.includes('display=swap')) {
        const separator = href.includes('?') ? '&' : '?';
        link.setAttribute('href', `${href}${separator}display=swap`);
      }
    });
  }

  /**
   * Lazy load non-critical resources
   */
  lazyLoadNonCriticalResources(): void {
    // Use requestIdleCallback to load non-critical resources (with iOS Safari fallback)
    safeRequestIdleCallback(() => {
      this.loadNonCriticalResources();
    });
  }

  /**
   * Load non-critical resources
   */
  private loadNonCriticalResources(): void {
    const nonCriticalResources: PreloadResource[] = [
      // Non-critical images
      {
        href: '/assets/images/logo white blue light.webp',
        as: 'image'
      },
      // Additional fonts
      {
        href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuI6fAZ9hiA.woff2',
        as: 'font',
        type: 'font/woff2',
        crossorigin: 'anonymous'
      }
    ];

    this.preloadResources(nonCriticalResources);
  }

  /**
   * Monitor Core Web Vitals
   */
  monitorWebVitals(): void {
    if (process.env.NODE_ENV === 'production') {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        const sendToAnalytics = (metric: any) => {
          // Send to your analytics service
          console.log('Web Vital:', metric);
          
          // Example: Send to Google Analytics
          if ('gtag' in window) {
            (window as any).gtag('event', metric.name, {
              event_category: 'Web Vitals',
              event_label: metric.id,
              value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
              non_interaction: true,
            });
          }
        };

        try {
          getCLS(sendToAnalytics);
          getFID(sendToAnalytics);
          getFCP(sendToAnalytics);
          getLCP(sendToAnalytics);
          getTTFB(sendToAnalytics);
        } catch (error) {
          console.warn('Web Vitals monitoring failed (likely iOS Safari compatibility issue):', error);
        }
      }).catch((error) => {
        console.warn('Failed to load web-vitals library:', error);
      });
    }
  }
}

export const preloadService = new PreloadService();